import React from 'react';
import styled from 'styled-components';
import toolClient from '@requests/telnyx/tools.requests';

import { AIAssistant } from '@requests/telnyx';
import { Label } from '@common/Label';
import { useQuery } from '@tanstack/react-query';
import { RequestError } from '@requests/common';
import { AITool } from '@components/Workspace/routes/Assistants/Assistant/AssistantTools/tool.types';
import { Loader } from '@common/hooks/useLoader';
import { Stack } from '@mui/material';
import { Card } from '@common/Card';
import Markdown from '@common/Node/CompletionChatbot/Markdown';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

interface Props {
  assistants: AIAssistant[];
  system: string;
}

export const AssistantRouterSystem: React.FC<Props> = ({ assistants, system }) => {
  const { data: flowTools = [], isLoading } = useQuery<AITool[], RequestError>({
    queryKey: ['ai_assistants_tools'],
    queryFn: toolClient.listAll,
    retry: false,
  });

  if (isLoading) {
    return (
      <Container>
        <Label
          title='System Message Rendered'
          tooltip='This is the system prompt that will be used to select the best assistant. The tools for each selected assistant will be added dynamically as shown below.'
        />
        <pre>
          <Stack width='100%' height='100%' justifyContent='center' alignItems='center'>
            <Loader />
          </Stack>
        </pre>
      </Container>
    );
  }

  const assistantToolName = (name: string) => {
    let formatted = name.replace(/ /g, '_');
    formatted = formatted.replace(/[^a-zA-Z0-9_\-]/g, '');
    return formatted.toLowerCase();
  };

  const formatAssistantSystem = () => {
    const behaviourHeader = [
      '# Tool Instructions',
      'Each assistant is a tool you can use to answer the question. These assistants have access to their own tools that can only be used after the assistant is selected. You do not have access to the tools in the tables below. If an assistant is applicable to the question, you should always run the assistant tool before generating an answer.',
    ].join('\n');

    const systemContent = system ? ['# Instructions', system, '\n', behaviourHeader] : [behaviourHeader];

    for (const assistant of assistants) {
      const assistantHeader = [
        `## Assistant Tool: \`${assistantToolName(assistant.name)}\``,
        `${assistant.description}`,
        '',
      ].join('\n');

      let toolsTable = '';

      const assistantTools = flowTools.filter((tool) => tool.assistant_id === assistant.id);
      if (assistantTools.length > 0) {
        toolsTable = ['| Tool Name | Description |', '| --- | --- |']
          .concat(assistantTools.map((tool) => `| \`${tool.name}\` | ${tool.description.replace(/\n/g, ' ')} |`))
          .join('\n');
      }

      systemContent.push(`${assistantHeader}${toolsTable}\n`);
    }

    return systemContent.join('\n');
  };

  return (
    <Container>
      <Label
        title='System Message Rendered'
        tooltip='This is the system prompt that will be used to select the best assistant. The tools for each selected assistant will be added dynamically as shown below.'
      />
      <Card body={<Markdown content={formatAssistantSystem()} />} />
    </Container>
  );
};
